import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'projects/ai_modernization/src/environments/environment';
import { RejectionReason } from '../../dashboard/dashboard.models';

export interface Assignment {
    appId: string;
    trackedBusinessApplicationId: number;
    assignedTo: string;
    executionAssignee: string | null;
    assignedRole: string;
    assignedByOperationManagerEmail: string | null;
    notes: string;
    consentTimestamp: Date | null;
}

export interface Rejection {
    appId: string;
    trackedBusinessApplicationId: number;
    assessmentStatusId: number | null;
    modernizationStatusId: number | null;
    personaActionStatusValue: string | null;
    rejectionReasonId: number;
    rejectionDetail: string;
    rejectionTimestamp: Date | null;
}

export interface ApplicationHistory {
    id: number;
    appId: string;
    action: string;
    outcome: string;
    aiRecommendation: string;
    overrideReason: string;
    rejectionReason: string;
    rejectionCategory: string;
    rejectionDetail: string;
    rejectedBy: string;
    rejectedAt: Date;
    assignedTo: string;
    assignedByRole: string;
    createdBy: string;
    createdAt: Date;
}

export interface UserSearchResult {
    id: string;
    displayName: string;
    givenName: string;
    surname: string;
    mail: string;
    userPrincipalName: string;
    jobTitle: string;
    officeLocation: string;
    mobilePhone: string;
    preferredLanguage: string;
    businessPhones: string[];
}

export interface UserSearchResponse {
    '@odata.context': string;
    '@odata.count': number;
    '@odata.nextLink'?: string;
    value: UserSearchResult[];
}

export interface ModernizationPod {
    podId: number;
    name: string;
    isVisible: boolean;
}

export interface AssignPodRequest {
    appId: string;
    podId: number;
    notes: string;
    trackedBusinessApplicationId: number;
    assignedTo: string;
    consentTimestamp: Date | null;
}

export interface OverrideAssessmentPayload {
    appId: number;
    overrideAction: string;
    overrideReason: string;
}

export interface ApproveAssessmentPayload {
    appId: string;
    trackedBusinessApplicationId: number;
    consentTimestamp: Date | null;
}

export interface ApproveAssessmentResult {
    adoWorkItemId: number | null;
    adoWorkItemUrl: string | null;
    adoWorkItemOperation?: 'created' | 'updated' | null;
}

@Injectable({
    providedIn: 'root'
})
export class AssignmentService {
    constructor(private http: HttpClient) { }

    createAssignment(assignment: Assignment): Observable<Assignment> {
        return this.http.post<Assignment>(`${environment.apiUrl}/assignments/assign`, assignment);
    }

    getRejectionReasons(): Observable<RejectionReason[]> {
        return this.http.get<RejectionReason[]>(`${environment.apiUrl}/rejection/rejection-reason`);
    }

    searchUsers(searchTerm: string): Observable<UserSearchResponse> {
        return this.http.get<UserSearchResponse>(`${environment.apiUrl}/users/search?searchTerm=${searchTerm}`);
    }

    rejectApplication(rejection: Rejection): Observable<void> {
        return this.http.post<void>(`${environment.apiUrl}/rejection/save`, rejection);
    }
    getVisiblePods(): Observable<ModernizationPod[]> {
        return this.http.get<ModernizationPod[]>(`${environment.apiUrl}/pods`);
    }

    assignPod(request: AssignPodRequest): Observable<any> {
        return this.http.post(`${environment.apiUrl}/assignments/assign-pod`, request);
    }

    getApplicationHistory(appId: string): Observable<ApplicationHistory[]> {
        return this.http.get<ApplicationHistory[]>(`${environment.apiUrl}/rejection/history/${appId}`);
    }

    overrideAssessment(payload: OverrideAssessmentPayload): Observable<any> {
        return this.http.patch(`${environment.apiUrl}/assignments/override-assessment`, payload);
    }

    approveAssessment(payload: ApproveAssessmentPayload): Observable<ApproveAssessmentResult> {
        return this.http.post<ApproveAssessmentResult>(`${environment.apiUrl}/assignments/approve-assessment`, payload);
    }
}